
import { Button } from '@factorialco/deprecated-design-system'
import { DAY_TYPES } from 'config/Vars'
import { reaction } from 'mobx'

import { observer } from 'mobx-react'

import FormRow from 'modules/core/components/FormRow'
import RadioGroup from 'modules/core/components/Inputs/RadioGroup'
import Text from 'modules/core/components/Inputs/Text'
import Modal from 'modules/core/components/Modal'
import { useCallback } from 'modules/core/hooks/useCallback'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useLazyRef } from 'modules/core/hooks/useLazyRef'
import { useMemo } from 'modules/core/hooks/useMemo'
import asyncAction from 'modules/core/lib/asyncAction'
import i18n from 'modules/core/lib/i18n'
import { usePortal } from 'modules/core/lib/portalContext'

import CompanyHolidayForm from 'modules/timeoff/lib/CompanyHoliday'
import companyHolidays from 'modules/timeoff/store/collections/companyHolidays'
import CompanyHoliday from 'modules/timeoff/store/models/CompanyHoliday'

import { HalfDay } from './HalfDay'
import { OneDay } from './OneDay'

interface RemoveProps {
  holiday: CompanyHoliday | null | undefined
}

const RemoveButton = ({ holiday }: RemoveProps) => {
  const { closePortal } = usePortal()

  const onClick = async () => {
    if (!holiday) return

    await asyncAction(() => holiday.destroy())
    closePortal()
  }

  return (
    <Button onClick={onClick} type="secondary">
      {i18n.t('settings.time_off.remove_company_holidays.title')}
    </Button>
  )
}

interface Props {
  onClose: () => void
  locationId: string
  holidayId?: string
}

const AddHoliday = ({ onClose, locationId, holidayId }: Props) => {
  const holiday = useMemo(() => {
    return holidayId ? companyHolidays.get(holidayId) : null
  }, [holidayId])

  const dayType = useMemo(() => {
    if (!holiday) return DAY_TYPES.one_day
    return holiday.attributes.half_day ? DAY_TYPES.half_day : DAY_TYPES.one_day
  }, [])

  const attr = holiday ? holiday.toJS() : {}
  const companyForm = useLazyRef(
    () =>
      new CompanyHolidayForm({
        location_id: locationId,
        day_type: dayType,
        ...attr,
      })
  ).current

  useEffect(() => {
    reaction(
      () => companyForm.get('day_type'),
      (dayType) => {
        if (dayType.value === DAY_TYPES.one_day) {
          companyForm.setValues({
            half_day: CompanyHoliday.HalfDayTypes.beginning_of_day,
          })
        }
      },
      {
        fireImmediately: true,
      }
    )
  }, [companyForm.get('day_type').value, companyForm])

  const onSubmit = async (closePortal: () => void) => {
    if (companyForm.get('day_type').value === DAY_TYPES.one_day) {
      companyForm.setValues({
        half_day: null,
      })
    }

    const { success } = await asyncAction(() =>
      holiday ? companyForm.save(holiday) : companyForm.create(companyHolidays)
    )

    if (success) closePortal()
  }
  const renderDaySelector = useCallback(() => {
    const dayType = companyForm.get('day_type').value

    switch (dayType) {
      case DAY_TYPES.half_day:
        return <HalfDay form={companyForm} />

      case DAY_TYPES.one_day:
        return <OneDay form={companyForm} />

      default:
        throw new Error(`Invalid day type ${dayType}`)
    }
  }, [companyForm.get('day_type').value])

  return (
    <Modal
      illustration="calendar"
      title={i18n.t('settings.time_off.add_company_holidays.title')}
      description={i18n.t('settings.time_off.add_company_holidays.subtitle')}
      onSubmit={onSubmit}
      onClose={onClose}
      footer={
        <>
          {!holiday && (
            <Button submit>{i18n.t('settings.time_off.add_company_holidays.title')}</Button>
          )}

          {!!holiday && <RemoveButton holiday={holiday} />}
          {!!holiday && (
            <Button submit>
              {holiday
                ? i18n.t('settings.time_off.edit_company_holidays.title')
                : i18n.t('settings.time_off.add_company_holidays.title')}
            </Button>
          )}
        </>
      }
    >
      <FormRow>
        <Text label={i18n.t('settings.time_off.name')} field={companyForm.get('summary')} />
      </FormRow>
      <FormRow>
        <RadioGroup
          invisibleRadio
          name="dayType"
          theme="toggle"
          field={companyForm.get('day_type')}
          options={[
            {
              value: DAY_TYPES.half_day,
              label: i18n.t('time_off.day_types.half_day'),
            },
            {
              value: DAY_TYPES.one_day,
              label: i18n.t('time_off.day_types.one_day'),
            },
          ]}
        />
      </FormRow>
      {renderDaySelector()}
    </Modal>
  )
}

export default observer(AddHoliday)
